import { Component, OnInit, Input, Output } from "@angular/core";
import BaseComponent from "../../../../core/base-component";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { CepValidator } from "../../../../core/validators/cep.validator";
import { DocumentValidator } from "../../../../core/validators/document.validator";
import { cnpj } from "cpf-cnpj-validator";
import { catchError, delay, distinctUntilChanged, filter, take, tap } from "rxjs/operators";
import { ApiServiceStore } from "../../../../core/services/store.service";
import { NgxSpinnerService } from "ngx-spinner";
import { throwError } from "rxjs";
import { CloudService } from "../../../../core/services/cloud.service";
import { EventEmitter } from "events";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";

@Component({
  selector: "solicitation-store-dialog",
  templateUrl: "./solicitation-store-dialog.component.html",
  styleUrls: ["./solicitation-store-dialog.component.scss"]
})
export class SolicitationStoreDialogComponent extends BaseComponent implements OnInit {
  form: FormGroup;
  responsiveMobile: boolean = false;
  etapa_1: boolean = true;
  etapa_2: boolean = false;
  accept_terms: boolean = false;
  cepByCNPJ: boolean;

  @Input() selectedProduct;
  @Input() siteName: string;
  @Input() typeSelect: string = "ALL";
  @Output() openSignup = new EventEmitter();

  constructor(
    public matDialogRef: MatDialogRef<SolicitationStoreDialogComponent>,
    public dialog: MatDialog,
    private cloudService: CloudService,
    private formBuilder: FormBuilder,
    private apiServiceStore: ApiServiceStore,
    private spinner: NgxSpinnerService
  ) {
    super();
  }

  ngOnInit() {
    this.createForm();
    this.getWidth();
  }

  clearAddress() {
    this.field("address").reset();
  }

  createForm() {
    this.form = this.formBuilder.group({
      document: ["", Validators.compose([Validators.required, DocumentValidator.validarCnpjOrCpf])],
      product_uuid: ["", Validators.required],
      name: ["", Validators.required],
      phone: ["", Validators.required],
      whatsapp_phone: ["", Validators.required],
      description: [""],
      responsible: ["", Validators.required],
      email: ["", [Validators.required, Validators.email]],

      address: this.formBuilder.group({
        street: ["", Validators.required],
        number: ["", Validators.required],
        complement: [""],
        neighborhood: ["", Validators.required],
        city: ["", Validators.required],
        state: ["", Validators.required],
        zipcode: ["", Validators.compose([Validators.required, CepValidator.validateCep])]
      })
    });
  }

  private reset(): void {
    this.form.reset();
    this.clearAddress();
  }

  getWidth() {
    if (window.innerWidth <= 768) {
      this.responsiveMobile = true;
    }
  }

  get formProduct() {
    return this.form.value;
  }

  toggleStep() {
    if (this.responsiveMobile) {
      if (this.etapa_2) {
        this.etapa_1 = true;
        this.etapa_2 = false;
        return;
      }
      this.etapa_1 = false;
      this.etapa_2 = true;
    }
  }

  getCNPJ() {
    const value = this.field("document").value.replace(/\D/g, "") as string;
    this.cloudService
      .getCNPJ(value)
      .pipe(
        filter(() => value.length > 7),
        delay(2000),
        tap(res => {
          this.cepByCNPJ = true;
          const phones = res.data.telefone.split("/");
          const phoneDefault = phones[0] ? phones[0].replace(/\D/g, "") : "";
          const phoneOptional = phones[1] ? phones[1].replace(/\D/g, "") : "";

          this.field("name").setValue(res.data.nome);
          this.field("phone").setValue(phoneDefault);
          this.field("whatsapp_phone").setValue(phoneOptional);

          this.field("address.zipcode").setValue(res.data.cep.replace(/\D/g, ""));
          this.field("address.street").setValue(res.data.logradouro);
          this.field("address.number").setValue(res.data.numero);
          this.field("address.city").setValue(res.data.municipio);
          this.field("address.neighborhood").setValue(res.data.bairro);
          this.field("address.state").setValue(res.data.uf);
          this.field("address.complement").setValue(res.data.complemento);
        }),
        take(1)
      )
      .subscribe();
  }

  getCep() {
    const zipcode = this.field("address.zipcode");
    const value = zipcode.value.replace(/\D/g, "") as string;
    return this.apiServiceStore
      .getCep(value)
      .pipe(
        filter(() => value.length > 7),
        distinctUntilChanged(),
        tap(res => {
          this.field("address.street").setValue(res.street);
          this.field("address.city").setValue(res.city);
          this.field("address.neighborhood").setValue(res.neighborhood);
          this.field("address.state").setValue(res.state);
        }),
        catchError(err => {
          this.spinner.hide();
          switch (err.status) {
            case 404:
              this.alert("CEP inválido", "warning");
              zipcode.setErrors({ cepInvalid: true });
              this.clearAddress();
              break;
            default:
              this.alert("Erro ao buscar o cep", "error");
              zipcode.setErrors({ cepInvalid: false });
              break;
          }
          return throwError(err);
        }),
        take(1)
      )
      .subscribe();
  }

  closeDialog(close: boolean) {
    this.matDialogRef.close({ close: close });
  }

  selectProduct() {
    this.field("product_uuid").setValue(this.selectedProduct.uuid);
  }

  send() {
    this.selectProduct();

    if (!this.form.valid) {
      this.form.markAllAsTouched();
      this.alert("Formulário inválido", "warning");
      return;
    }
    if (!this.accept_terms) {
      this.alert("Aceite os termos de uso", "error");
      return;
    }

    this.spinner.show();
    const formData = this.form.value;
    const { ...address } = formData.address;
    const data = { ...formData, address };

    delete data.description;

    data.document = cnpj.strip(data.document);
    data.document_type = cnpj.isValid(data.document) ? "CNPJ" : "CPF";

    this.apiServiceStore
      .dealStorePublic(this.siteName, data)
      .pipe(
        tap(() => {
          this.alert("Solicitação enviada com sucesso", "success");
          this.spinner.hide();
          this.reset();
          this.matDialogRef.close(SolicitationStoreDialogComponent);
        }),
        catchError(error => {
          this.spinner.hide();
          return throwError(() => {
            this.applyErrors(error.errors);
            this.alert("Formulário inválido", "warning");
          });
        })
      )
      .subscribe();
  }
}
